import { CLICK_PATH_DELAY_MS, MAX_CLICK_PATH_LENGTH, SCROLL_END_DELAY_MS } from '../enum/FlagshipConstant';
import { TroubleshootingLabel } from '../types';
import { onDomReady } from '../utils/utils';
import { CommonEmotionAI } from './CommonEmotionAI';
import { PageView } from './hit/PageView';
import { VisitorEvent } from './hit/VisitorEvent';
export class EmotionAI extends CommonEmotionAI {
    onScroll;
    onMouseMove;
    onMouseDown;
    onMouseUp;
    onPopState;
    _originalPushState;
    _originalReplaceState;
    _clickPath = '';
    scrollTimeoutId = null;
    scrollEndDelay = 200;
    _clickPathTimeoutId = null;
    _lastPageViewLocation;
    constructor(params) {
        super(params);
        this._originalPushState = window.history.pushState;
        this._originalReplaceState = window.history.replaceState;
    }
    cleanup() {
        this.removeListeners();
        this._isEAIDataCollecting = false;
        clearInterval(this._scoringIntervalId);
    }
    getDeviceCategory() {
        const userAgent = navigator.userAgent.toLowerCase();
        if (/iphone|ipad|ipod/.test(userAgent)) {
            return 'iphone';
        }
        if (/android/.test(userAgent)) {
            return 'android';
        }
        if (/windows nt/.test(userAgent)) {
            return 'win32';
        }
        if (/macintosh|mac os x/.test(userAgent)) {
            return 'darwin';
        }
        if (/linux/.test(userAgent)) {
            if (/armv8l/.test(userAgent)) {
                return 'linux armv8l';
            }
            return 'linux';
        }
        return 'unknown';
    }
    async processPageView(visitorId) {
        if (this._lastPageViewLocation === window.location.href) {
            return;
        }
        this._lastPageViewLocation = window.location.href;
        const maxTouchPoints = navigator.maxTouchPoints || 0;
        const touchEvent = 'ontouchstart' in window;
        const touchStart = 'ontouchstart' in window || 'onmsgesturechange' in window;
        const touchSupport = JSON.stringify([maxTouchPoints, touchEvent, touchStart]);
        const pageView = new PageView({
            visitorId,
            customerAccountId: this._sdkConfig.envId,
            currentUrl: window.location.href,
            hasAdBlocker: false,
            screenDepth: `${window.screen.colorDepth}`,
            screenSize: `${window.innerWidth},${window.innerHeight};`,
            doNotTrack: navigator.doNotTrack || 'unspecified',
            fonts: '[]',
            hasFakeBrowserInfos: false,
            hasFakeLanguageInfos: false,
            hasFakeOsInfos: false,
            hasFakeResolutionInfos: false,
            userLanguage: navigator.language,
            deviceCategory: this.getDeviceCategory(),
            pixelRatio: window.devicePixelRatio,
            documentReferer: document.referrer,
            viewportSize: `[${document.documentElement.clientWidth},${document.documentElement.clientHeight}]`,
            timezoneOffset: new Date().getTimezoneOffset(),
            touchSupport,
            eventCategory: 'click tunnel auto',
            userAgent: navigator.userAgent
        });
        await this.reportPageView(pageView);
    }
    async startCollectingEAIData(visitorId) {
        this._isEAIDataCollecting = true;
        await this.processPageView(visitorId);
        this._startCollectingEAIDataTimestamp = Date.now();
        this.sendCollectingTroubleshooting(this._startCollectingEAIDataTimestamp, TroubleshootingLabel.EMOTION_AI_START_COLLECTING);
        this.sendCollectingUsageHit(TroubleshootingLabel.EMOTION_AI_START_COLLECTING);
        let mouseDownTimestamp = null;
        this.onScroll = () => this.handleScroll(visitorId);
        this.onMouseMove = (event) => this.handleMouseMove(event, visitorId);
        this.onMouseDown = () => {
            mouseDownTimestamp = Date.now();
        };
        this.onMouseUp = (event) => {
            if (mouseDownTimestamp) {
                const clickDuration = Date.now() - mouseDownTimestamp;
                this.handleClick(event, visitorId, clickDuration);
                mouseDownTimestamp = null;
            }
        };
        this.onPopState = () => {
            this.processPageView(visitorId);
        };
        onDomReady(() => {
            window.history.pushState = (...args) => {
                this._originalPushState.apply(window.history, args);
                this.processPageView(visitorId);
            };
            window.history.replaceState = (...args) => {
                this._originalReplaceState.apply(window.history, args);
                this.processPageView(visitorId);
            };
            window.addEventListener('scroll', this.onScroll);
            document.addEventListener('mousemove', this.onMouseMove);
            document.addEventListener('mousedown', this.onMouseDown);
            document.addEventListener('mouseup', this.onMouseUp);
            window.addEventListener('popstate', this.onPopState);
        });
    }
    removeListeners() {
        onDomReady(() => {
            window.removeEventListener('scroll', this.onScroll);
            document.removeEventListener('mousemove', this.onMouseMove);
            document.removeEventListener('mousedown', this.onMouseDown);
            document.removeEventListener('mouseup', this.onMouseUp);
            window.removeEventListener('popstate', this.onPopState);
            window.history.pushState = this._originalPushState;
            window.history.replaceState = this._originalReplaceState;
        });
        if (this.scrollTimeoutId !== null) {
            clearTimeout(this.scrollTimeoutId);
            this.scrollTimeoutId = null;
        }
        if (this._clickPathTimeoutId !== null) {
            clearTimeout(this._clickPathTimeoutId);
            this._clickPathTimeoutId = null;
        }
    }
    onScrollEnd(visitorId) {
        const timestamp = Date.now().toString().slice(-5);
        const scrollPosition = `${window.scrollY},${timestamp};`;
        const visitorEvent = new VisitorEvent({
            visitorId,
            customerAccountId: this._sdkConfig.envId,
            scrollPosition,
            screenSize: `${window.innerWidth},${window.innerHeight};`,
            currentUrl: window.location.href
        });
        this.reportVisitorEvent(visitorEvent);
    }
    handleScroll = (visitorId) => {
        if (this.scrollTimeoutId !== null) {
            clearTimeout(this.scrollTimeoutId);
        }
        this.scrollTimeoutId = window.setTimeout(() => {
            this.onScrollEnd(visitorId);
            this.scrollTimeoutId = null;
        }, SCROLL_END_DELAY_MS);
    };
    sendClickPath(visitorId) {
        const visitorEvent = new VisitorEvent({
            visitorId,
            customerAccountId: this._sdkConfig.envId,
            clickPath: this._clickPath,
            screenSize: `${window.innerWidth},${window.innerHeight};`,
            currentUrl: window.location.href
        });
        this._clickPath = '';
        this.reportVisitorEvent(visitorEvent);
    }
    handleMouseMove = (event, visitorId) => {
        if (this._clickPathTimeoutId !== null) {
            clearTimeout(this._clickPathTimeoutId);
        }
        this._clickPath += `${event.clientX},${event.clientY},${Date.now().toString().slice(-5)};`;
        if (this._clickPath.length >= MAX_CLICK_PATH_LENGTH) {
            this.sendClickPath(visitorId);
            return;
        }
        this._clickPathTimeoutId = window.setTimeout(() => {
            this.sendClickPath(visitorId);
        }, CLICK_PATH_DELAY_MS);
    };
    handleClick = (event, visitorId, clickDuration) => {
        const timestamp = Date.now().toString().slice(-5);
        const visitorEvent = new VisitorEvent({
            visitorId,
            customerAccountId: this._sdkConfig.envId,
            clickPosition: `${event.clientX},${event.clientY},${timestamp},${clickDuration};`,
            screenSize: `${window.innerWidth},${window.innerHeight};`,
            currentUrl: window.location.href
        });
        this.reportVisitorEvent(visitorEvent);
    };
}
